import React from 'react';
import type { PhoneCountryCode } from '@shared/utils/phone';
import type { NewRegisterScreenProps } from './types/descope';

export const OPTIMISTIC_FLOW_READY_TIMEOUT_MS = 5000;

type SignupHandler =
  'onClickContinue' | 'onClickGoogle' | 'onClickApple' | 'onClickSMS';
type SignupInvocation =
  | { handler: Exclude<SignupHandler, 'onClickSMS'> }
  | {
      handler: 'onClickSMS';
      phone: string;
      country?: PhoneCountryCode;
    };

type WaitResult<T> = {
  value: T | undefined;
  outcome: 'ready' | 'timeout' | 'cancelled';
};

export const useResolveFlowReady = (
  isReady: boolean,
  resolve: (() => void) | undefined
): void => {
  React.useEffect(() => {
    if (isReady) {
      resolve?.();
    }
  }, [isReady, resolve]);
};

export const getSignupGateState = (
  isSignupScreen: boolean,
  showOptimisticSignup: boolean,
  isPending: boolean,
  isEnabled: boolean
): {
  shouldRender: boolean;
  isPending: boolean;
  isEnabled: boolean;
} => ({
  shouldRender: isSignupScreen || showOptimisticSignup,
  isPending: !showOptimisticSignup && isPending,
  isEnabled: showOptimisticSignup || isEnabled,
});

export const isOptimisticSignupVisible = (
  optimisticSignupEntry: boolean,
  screenName?: string
): boolean => optimisticSignupEntry && !screenName;

export const waitForSignupFlow = async <T>(
  getReadyValue: () => T | undefined,
  ready: Promise<void>,
  timeoutMs = OPTIMISTIC_FLOW_READY_TIMEOUT_MS,
  cancelled: Promise<void> = new Promise(() => undefined)
): Promise<WaitResult<T>> => {
  const currentValue = getReadyValue();
  if (currentValue !== undefined) {
    return { value: currentValue, outcome: 'ready' };
  }

  let timeoutId: number | undefined;
  const timeout = new Promise<'timeout'>(resolve => {
    timeoutId = window.setTimeout(() => resolve('timeout'), timeoutMs);
  });
  const outcome = await Promise.race([
    ready.then(() => 'ready' as const),
    timeout,
    cancelled.then(() => 'cancelled' as const),
  ]);
  if (timeoutId !== undefined) {
    window.clearTimeout(timeoutId);
  }

  return {
    value: outcome === 'ready' ? getReadyValue() : undefined,
    outcome,
  };
};

export const useOptimisticSignupProps = (
  enabled: boolean,
  liveProps: NewRegisterScreenProps,
  ready: Promise<void>,
  getReadyNext: () => unknown,
  onTimeout: (handler: SignupHandler) => void
): NewRegisterScreenProps => {
  const livePropsRef = React.useRef(liveProps);
  const cancelRef = React.useRef<(() => void) | undefined>(undefined);
  const cancelledRef = React.useRef(
    new Promise<void>(resolve => {
      cancelRef.current = resolve;
    })
  );
  React.useEffect(() => {
    livePropsRef.current = liveProps;
  }, [liveProps]);
  React.useEffect(
    () => () => {
      cancelRef.current?.();
    },
    [enabled]
  );

  if (!enabled) {
    return liveProps;
  }

  const runWhenReady = async (invocation: SignupInvocation): Promise<void> => {
    const result = await waitForSignupFlow(
      getReadyNext,
      ready,
      OPTIMISTIC_FLOW_READY_TIMEOUT_MS,
      cancelledRef.current
    );
    if (result.outcome === 'timeout') {
      onTimeout(invocation.handler);
      return;
    }

    if (result.outcome === 'ready' && result.value !== undefined) {
      if (invocation.handler === 'onClickSMS') {
        await livePropsRef.current.onClickSMS?.(
          invocation.phone,
          invocation.country
        );
      } else if (invocation.handler === 'onClickContinue') {
        await livePropsRef.current.onClickContinue?.();
      } else if (invocation.handler === 'onClickGoogle') {
        await livePropsRef.current.onClickGoogle?.();
      } else {
        await livePropsRef.current.onClickApple?.();
      }
    }
  };

  return {
    ...liveProps,
    onClickContinue: () => runWhenReady({ handler: 'onClickContinue' }),
    onClickGoogle: () => runWhenReady({ handler: 'onClickGoogle' }),
    onClickApple: () => runWhenReady({ handler: 'onClickApple' }),
    onClickSMS: (phone: string, country?: PhoneCountryCode) =>
      runWhenReady({ handler: 'onClickSMS', phone, country }),
    publishViewEvents: false,
  };
};
